import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import DataCacheService from '@/shared/services/DataCacheService';
import intlShape from '@/shared/utils/intlShape';
import RelationTypeIcon from './RelationTypeIcon';
import { injectStateService, StateService } from '../services/StateService';
import { listItemMsg } from './FormattedListValue';
import AuthService from '../services/AuthService';
import { mapInvoiceStreamName } from '../services/ObjectDisplayService';
import RelationHandlingLevel from '@/shared/components/RelationHandlingLevel';
import { isDutchTenantConfiguration } from '@/shared/TenantConfigurationConstants';

// NL legal form codes that are shown in front of the relation name (e.g. "Maatschap Acme"): Maatschap
const LEGAL_FORMS_PREPENDED_TO_NAME = new Set([129]);

const messages = defineMessages({
  GoToRelation: 'Go to relation',
  DeleteRelation: 'Delete relation',
});

export const getRelationName = (intl, relation) => {
  if (!intl || !intl.formatMessage || !relation || !relation.name) return null;

  const lastName = relation.infix ? `${relation.infix} ${relation.name}` : `${relation.name}`;
  const name = relation.firstName ? `${lastName} ${relation.firstName}` : relation.name;

  // if DisplayName is filled in, the call comes from Monolyth, if it comes from the microservices, DisplayName will be empty, but Name is filled with value of DisplayName by RelationChangedEventModel
  const displayName = relation.displayName !== undefined ? relation.displayName : name;

  if (!relation.title || relation.title === '0') return displayName;
  const isPerson = relation.type !== 0;

  const titleType = isPerson ? 'salutation' : 'legalForm';
  const dataCache = DataCacheService.getDataCache();
  if (!dataCache || !dataCache[`${titleType}Object`]) return displayName; // Cache not ready

  const title = dataCache[`${titleType}Object`][relation.title];
  if (!title) return displayName;
  if (isPerson && !title.show) return displayName;
  // For NL tenants these legal forms always show in front of the name, even when the legal form
  // isn't configured to append. The stored name is unchanged, so server-side sorting still uses
  // the name without the prefix.
  const isPrependedLegalForm =
    !isPerson &&
    LEGAL_FORMS_PREPENDED_TO_NAME.has(Number(relation.title)) &&
    isDutchTenantConfiguration(AuthService.tenantConfiguration());

  if (!isPerson && !title.appendLegalFormToName && !isPrependedLegalForm) return displayName;
  const translatedTitle = intl.formatMessage(
    listItemMsg(titleType, relation.title, title.description || ''),
  );
  if (isPerson) return `${translatedTitle} ${displayName}`.trim();

  if (isPrependedLegalForm) {
    // Skip the prefix only when the name already contains the legal form as a whole word,
    // so e.g. "Maatschappij" still becomes "Maatschap Maatschappij".
    const escapedTitle = translatedTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (escapedTitle && new RegExp(`\\b${escapedTitle}\\b`, 'i').test(displayName)) {
      return displayName;
    }
    return `${translatedTitle} ${displayName}`.trim();
  }

  return `${displayName} ${translatedTitle}`.trim();
};
export const getRelationNameMapper = intl => relation => getRelationName(intl, relation);

export const getRelationNameCode = (intl, relation) => {
  if (!intl || !intl.formatMessage || !relation || !relation.name) return null;

  const relationName = getRelationName(intl, relation);
  if (!relation.customerCode) {
    return `${relationName} | ${relation.uniqueIdentifier}`;
  }

  return `${relationName} | ${relation.customerCode}`;
};

export const getRelationCode = relation =>
  (relation && (relation.customerCode || relation.uniqueIdentifier)) || null;

// Ids of the relations whose display name occurs more than once in the list, so the code can be shown to tell them apart
export const getDuplicateRelationNameIds = (relations, getName) => {
  const named = (relations || [])
    .map(relation => ({ id: relation.id, name: (getName(relation) || '').trim().toLowerCase() }))
    .filter(({ name }) => name);
  const countByName = new Map();
  named.forEach(({ name }) => countByName.set(name, (countByName.get(name) || 0) + 1));
  return new Set(named.filter(({ name }) => countByName.get(name) > 1).map(({ id }) => id));
};
const RelationName = ({
  intl,
  relation,
  stateService,
  enabled,
  tabIndex,
  onDeleteRelation,
  assignment,
  defaultAssignmentName,
  extraIcons,
}) => {
  if (relation === null) return null;
  const handleClick = e => {
    if (e.ctrlKey) return;
    stateService.gotoRelation(relation.id);
    e.preventDefault();
  };
  const relationName = relation ? getRelationName(intl, relation) : null;
  const assignmentName = assignment
    ? mapInvoiceStreamName(assignment, intl, defaultAssignmentName)
    : null;

  const fullName = assignmentName ? `${relationName} - ${assignmentName}` : relationName;

  return enabled ? (
    <span className="clickable relation-name overflow-ellipsis">
      <a
        href={stateService.gotoRelationHref(relation.id)}
        tabIndex={tabIndex}
        onClick={handleClick}
        title={intl.formatMessage(messages.GoToRelation)}
        id={`NameId${relation.name}${relation.id}`}
      >
        <RelationTypeIcon type={relation.type} crm={relation.crm} isActive={relation.isActive} />
        <RelationHandlingLevel
          handlingLevel={relation.relationHandlingLevel}
          clarification={relation.relationHandlingLevelClarification}
        />
        {extraIcons}
        &nbsp;
        {fullName}
      </a>
      {onDeleteRelation && (
        <i
          onClick={() => onDeleteRelation(relation.id)}
          title={intl.formatMessage(messages.DeleteRelation)}
          className="fa fa-trash-o margin5L clickable bold"
        />
      )}
    </span>
  ) : (
    <span className="">
      <RelationTypeIcon type={relation.type} crm={relation.crm} isActive={relation.isActive} />
      <RelationHandlingLevel
        handlingLevel={relation.relationHandlingLevel}
        clarification={relation.relationHandlingLevelClarification}
      />
      &nbsp;
      {fullName}
    </span>
  );
};

RelationName.propTypes = {
  intl: intlShape.isRequired,
  tabIndex: PropTypes.number,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  enabled: PropTypes.bool,
  relation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    infix: PropTypes.string,
    firstName: PropTypes.string,
    alias: PropTypes.string,
    crm: PropTypes.number,
    isActive: PropTypes.bool,
    relationHandlingLevel: PropTypes.number,
    relationHandlingLevelClarification: PropTypes.string,
  }),
  assignment: PropTypes.shape({
    assignmentId: PropTypes.string,
    assignmentName: PropTypes.string,
    assignmentTemplateId: PropTypes.string,
  }),
  defaultAssignmentName: PropTypes.string,
  onDeleteRelation: PropTypes.func,
  extraIcons: PropTypes.node,
};

RelationName.defaultProps = {
  relation: null,
  enabled: true,
  tabIndex: null,
  onDeleteRelation: null,
  assignment: null,
  defaultAssignmentName: '',
  extraIcons: null,
};

export default injectStateService(injectIntl(memo(RelationName)));
